import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellSimpleRinging, BellSimpleSlash, PaperPlaneTilt } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  pushSupported,
  enableReminder,
  disableReminder,
  getStoredReminder,
  sendTestReminder,
} from "@/lib/push";

export default function ReminderDialog() {
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState("07:00");
  const [reminder, setReminder] = useState(null);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    const stored = getStoredReminder();
    if (stored) {
      setReminder(stored);
      setTime(stored.time);
    }
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await enableReminder(time);
      setReminder(res);
      toast.success(`Daily reminder set for ${time}`);
    } catch (e) {
      toast.error(e.message || "Could not enable reminder");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disableReminder();
      setReminder(null);
      toast.success("Daily reminder turned off");
    } catch (e) {
      toast.error("Could not turn off reminder");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    try {
      await sendTestReminder();
      toast.success("Test nudge sent — check your notifications");
    } catch (e) {
      toast.error(e.message || "Could not send test");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="reminder-open-button"
          title="Daily reminder"
          className="relative text-stone-400 hover:text-orange-600 transition-colors duration-150"
        >
          <BellSimpleRinging size={20} weight={reminder ? "fill" : "regular"} />
          {reminder && (
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-orange-600" />
          )}
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid="reminder-dialog"
        className="bg-stone-900 border-stone-800 rounded-sm text-stone-50 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-stone-50">
            Daily Reminder
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-sm">
            Get a push notification when your 5 extracts are ready — even when the app is closed.
          </DialogDescription>
        </DialogHeader>

        {!supported ? (
          <p className="text-sm text-stone-400 py-4" data-testid="reminder-unsupported">
            Push notifications aren't supported on this browser. Try Chrome, Edge or Firefox on
            desktop or Android.
          </p>
        ) : (
          <div className="space-y-6 pt-2">
            <div>
              <label className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-orange-600">
                Notify me at
              </label>
              <input
                data-testid="reminder-time-input"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-2 w-full bg-stone-950 border border-stone-700 rounded-sm px-4 py-3 text-lg font-mono-ui text-stone-50 focus:outline-none focus:border-orange-700"
              />
            </div>

            {reminder ? (
              <div className="space-y-3">
                <div
                  data-testid="reminder-active-state"
                  className="flex items-center gap-2 text-sm text-stone-300 border border-stone-800 rounded-sm px-4 py-3"
                >
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  Reminder active for{" "}
                  <span className="font-mono-ui text-orange-500">{reminder.time}</span>
                </div>
                <div className="flex gap-3">
                  <button
                    data-testid="reminder-update-button"
                    onClick={handleEnable}
                    disabled={busy}
                    className="flex-1 border border-stone-700 hover:border-orange-700 rounded-sm px-4 py-3 text-xs font-mono-ui uppercase tracking-[0.15em] text-stone-200 transition-colors disabled:opacity-50"
                  >
                    Update time
                  </button>
                  <button
                    data-testid="reminder-disable-button"
                    onClick={handleDisable}
                    disabled={busy}
                    className="flex items-center justify-center gap-2 border border-stone-700 hover:border-red-800 hover:text-red-500 rounded-sm px-4 py-3 text-xs font-mono-ui uppercase tracking-[0.15em] text-stone-400 transition-colors disabled:opacity-50"
                  >
                    <BellSimpleSlash size={16} /> Off
                  </button>
                </div>
                <button
                  data-testid="reminder-test-button"
                  onClick={handleTest}
                  className="w-full flex items-center justify-center gap-2 text-stone-500 hover:text-stone-200 text-xs font-mono-ui uppercase tracking-[0.15em] transition-colors pt-1"
                >
                  <PaperPlaneTilt size={15} /> Send a test nudge
                </button>
              </div>
            ) : (
              <button
                data-testid="reminder-enable-button"
                onClick={handleEnable}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-orange-800 hover:bg-orange-700 rounded-sm px-4 py-3.5 text-xs font-mono-ui uppercase tracking-[0.2em] text-stone-50 transition-colors disabled:opacity-50"
              >
                <BellSimpleRinging size={17} weight="fill" />
                {busy ? "Enabling..." : "Enable daily reminder"}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
